import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useLocationScope } from "@/hooks/useLocationScope";
import { useRegistroMapaOff } from "@/hooks/useRegistroMapaOff";
import { fetchRegistryCatalog } from "@/hooks/useRegistryCatalog";
import { claveAlcance } from "@/lib/alcance";
import { buildCuaderno, cargarBorrador, loadCuadernoEntrada } from "@/lib/cuaderno/loader";
import { guardaDeSalida } from "@/lib/cuaderno/guardaSalida";
import { huellaDatos, ultimaVersion } from "@/lib/cuaderno/congelar";
import { borradorDeSeleccion, claveSeleccion, entradaDeSeleccion, type SeleccionCuaderno } from "@/lib/cuaderno/seleccion";

export interface CuadernoJuntaArgs {
  year: number;
  /** null = el titular de la empresa (D14). */
  titularLocationId: string | null;
  /** false = no se carga nada (sin interruptor o sin rol: no se leen partes por si acaso). */
  enabled?: boolean;
  /** El cuaderno de una SELECCIÓN (28-09-2026): solo esas líneas y sus valores, en memoria. */
  seleccion?: SeleccionCuaderno | null;
}

/** Clave de react-query de todo lo del cuaderno: invalidarla lo recalcula. */
export const CUADERNO_JUNTA_KEY = "cuaderno-junta";

/**
 * El cuaderno de un titular y un año, listo para enseñar y descargar (CQ4a):
 * carga los partes (con el alcance de quien mira, A16), el borrador y la última
 * versión congelada, lo monta con el motor y pasa la guarda de salida. Lo usan
 * la tarjeta del asistente y la hoja de /spray-logs: el MISMO cálculo.
 *
 * No sale hasta saber el alcance y el interruptor del MAPA: con el alcance a
 * medias, un encargado vería por un instante el cuaderno de toda la empresa.
 */
export function useCuadernoJunta({ year, titularLocationId, enabled = true, seleccion = null }: CuadernoJuntaArgs) {
  const { company } = useAuth();
  const { allowedLocationIds, excludedBlockIds, loading: cargandoAlcance } = useLocationScope();
  const { off: mapaOff, loading: cargandoMapa } = useRegistroMapaOff();
  const queryClient = useQueryClient();
  const companyId = company?.id ?? null;

  const query = useQuery({
    queryKey: [CUADERNO_JUNTA_KEY, companyId, titularLocationId ?? "empresa", year, claveAlcance(allowedLocationIds, excludedBlockIds), mapaOff, claveSeleccion(seleccion)],
    enabled: enabled && !!companyId && !cargandoAlcance && !cargandoMapa,
    staleTime: 60_000,
    queryFn: async () => {
      const catalogo = mapaOff ? null : await fetchRegistryCatalog(queryClient);
      const [entrada, borrador, ultima] = await Promise.all([
        loadCuadernoEntrada(supabase, {
          companyId,
          titularLocationId,
          year,
          allowedLocationIds,
          excludedBlockIds,
          catalogo,
          mapaValidation: !mapaOff,
        }),
        // El borrador, por (empresa, titular, año): la terna de `ensure_cuaderno_draft`.
        cargarBorrador(supabase, { companyId, titularLocationId, year }),
        // Sin versiones (o sin la tabla todavía) no se para nada: solo no se avisa.
        ultimaVersion(supabase, { companyId, titularLocationId, year }).catch(() => null),
      ]);
      /* Una selección se monta EN MEMORIA encima del borrador (seleccion.ts): el
         cuaderno del año no cambia y no se congela ninguna versión de ella. */
      const build = seleccion
        ? buildCuaderno(entradaDeSeleccion(entrada, seleccion), borradorDeSeleccion(borrador, seleccion))
        : buildCuaderno(entrada, borrador);
      // CU9: la entrada EFECTIVA (con las líneas que añadió la persona), la misma con la que se montó.
      const guarda = guardaDeSalida({ data: build.data, meta31: build.meta31, entrada: build.entrada, fuera: build.fuera });
      const { sha256: dataSha256 } = await huellaDatos(build.data);
      return { entrada, borrador, build, guarda, ultima, dataSha256 };
    },
  });

  return {
    ...query,
    companyId,
    /** Fincas incluidas si quien mira está acotado; null = todas las del titular. */
    alcanceFincas: allowedLocationIds && query.data ? query.data.entrada.fincas.map((f) => f.id) : null,
    acotado: !!allowedLocationIds,
  };
}
